import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trophy, Award, Star, Target } from 'lucide-react';

// 25% Milestone
export function Milestone25Modal({ isOpen, onClose, progress }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti from TOP corners */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={100}
            gravity={0.3}
            initialVelocityX={5}
            initialVelocityY={10}
            confettiSource={{
              x: 0,
              y: 0,
              w: windowSize.width,
              h: 10
            }}
            tweenDuration={800}
            colors={['#1E90FF', '#00CED1', '#FFD700', '#FF69B4', '#32CD32']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-8 relative z-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Target className="w-10 h-10 text-green-600" strokeWidth={2} />
            </div>
            
            <h2 className="text-4xl font-bold text-navy mb-3">🎉 Congratulations!</h2>
            <p className="text-lg text-[#4D4D4D] mb-6 font-light">
              Good head start — keep going!
            </p>
            
            <div className="bg-green-50 rounded-2xl py-4 px-6 mb-8">
              <p className="text-sm text-[#4D4D4D] mb-2">Course Progress</p>
              <p className="text-4xl font-bold text-green-600">{progress?.toFixed(0)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
            
            <Button
              onClick={onClose}
              className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press font-bold"
            >
              Continue Learning
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 50% Milestone
export function Milestone50Modal({ isOpen, onClose, progress }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti from TOP corners */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
            initialVelocityX={8}
            initialVelocityY={12}
            confettiSource={{
              x: 0,
              y: 0,
              w: windowSize.width,
              h: 10
            }}
            tweenDuration={1000}
            colors={['#1E90FF', '#00CED1', '#FFD700', '#FF69B4', '#32CD32', '#FF6347']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-8 relative z-10">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Star className="w-12 h-12 text-blue-600" strokeWidth={2} fill="#1E90FF" />
            </div>
            
            <h2 className="text-4xl font-bold text-navy mb-3">🌟 Halfway there — doing great!</h2>
            <p className="text-lg text-[#4D4D4D] mb-6 font-light">
              All GDIOTs are proud of you.
            </p>
            
            <div className="bg-blue-50 rounded-2xl py-4 px-6 mb-8">
              <p className="text-sm text-[#4D4D4D] mb-2">Course Progress</p>
              <p className="text-4xl font-bold text-primary-blue">{progress?.toFixed(0)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div className="bg-primary-blue h-2 rounded-full" style={{ width: `${progress}%` }}></div>
              </div>
              <p className="text-sm text-[#4D4D4D] mt-3">Next milestone: 75%</p>
            </div>
            
            <Button
              onClick={onClose}
              className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press font-bold sparkle-on-hover"
            >
              Keep Going ✨
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 75% Milestone - Special Workshop Reward
export function Milestone75Modal({ isOpen, onClose, progress }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti from TOP corners - Larger */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={300}
            gravity={0.3}
            initialVelocityX={10}
            initialVelocityY={15}
            confettiSource={{
              x: 0,
              y: 0,
              w: windowSize.width,
              h: 10
            }}
            tweenDuration={1200}
            colors={['#FFD700', '#FFA500', '#FF6347', '#1E90FF', '#32CD32', '#FF69B4', '#00CED1']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-10 relative z-10">
            <div className="w-28 h-28 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce shadow-lg">
              <Trophy className="w-16 h-16 text-white" strokeWidth={2} />
            </div>
            
            <h2 className="text-4xl font-bold text-navy mb-4">🏆 Congratulations!</h2>
            <p className="text-xl text-[#4D4D4D] mb-6 font-medium leading-relaxed">
              You are selected for the <span className="font-bold text-purple-600">OFFLINE workshop</span> in your school — you have earned it.<br />
              Continue learning!
            </p>
            
            <div className="bg-purple-50 rounded-2xl py-4 px-6 mb-8">
              <p className="text-sm text-[#4D4D4D] mb-2">Course Progress</p>
              <p className="text-4xl font-bold text-purple-600">{progress?.toFixed(0)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={onClose}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-lg rounded-2xl transition-smooth btn-press font-bold"
              >
                Claim Workshop 🎓
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-50 h-12 text-lg rounded-2xl transition-smooth font-bold"
              >
                Continue Learning
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// 100% Milestone - Course Complete
export function Milestone100Modal({ isOpen, onClose, progress, onViewLeaderboard }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti from TOP corners - BIGGEST */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={400}
            gravity={0.3}
            initialVelocityX={12}
            initialVelocityY={18}
            confettiSource={{
              x: 0,
              y: 0,
              w: windowSize.width,
              h: 10
            }}
            tweenDuration={1500}
            colors={['#FFD700', '#FFA500', '#FF6347', '#1E90FF', '#32CD32', '#FF69B4', '#00CED1', '#9370DB']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-12 relative z-10">
            <div className="relative mb-8">
              <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto animate-bounce shadow-2xl">
                <Trophy className="w-20 h-20 text-white" strokeWidth={2} />
              </div>
              <div className="absolute -top-4 left-0 right-0 mx-auto w-fit">
                <Award className="w-12 h-12 text-yellow-500 animate-spin" style={{ animationDuration: '3s' }} strokeWidth={2} />
              </div>
            </div>
            
            <h2 className="text-5xl font-bold text-navy mb-4">🎉 Congratulations!</h2>
            <p className="text-2xl text-[#4D4D4D] mb-3 font-bold">
              You completed your course — you're a master now!
            </p>
            <p className="text-lg text-[#4D4D4D] mb-8 font-light">
              Next adventure will be coming soon.<br />
              Go spot yourself on the leaderboard!
            </p>
            
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl py-6 px-6 mb-8 border-2 border-yellow-300">
              <p className="text-sm text-[#4D4D4D] mb-2">Course Progress</p>
              <p className="text-5xl font-bold text-yellow-600">{progress?.toFixed(0)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full" style={{ width: '100%' }}></div>
              </div>
              <p className="text-lg font-bold text-navy mt-4">🏆 Course Mastered!</p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={onViewLeaderboard}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white h-14 text-xl rounded-2xl transition-smooth btn-press font-bold shadow-lg"
              >
                Go to Leaderboard 🏆
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 h-12 text-lg rounded-2xl transition-smooth font-bold"
              >
                Browse Next Course
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
